/**
 * Hull Moving Average helpers (Pine `ta.wma` / `ta.hma` parity).
 *
 * Used by the AV_Band overlay (HMA of the Effective Line) and available for
 * any future HMA-based indicator. Operates on plain number[] series; entries
 * are NaN until enough finite inputs are available.
 */

/** Weighted Moving Average — linear weights 1..len (oldest→newest, newest
 *  heaviest). NaN until `len` finite values exist; NaN inputs void the window. */
export function wmaSeries(src: number[], len: number): number[] {
  const n = src.length;
  const out = new Array<number>(n).fill(NaN);
  if (len <= 0) return out;
  const denom = (len * (len + 1)) / 2;
  for (let i = 0; i < n; i++) {
    if (i < len - 1) continue;
    let wsum = 0;
    let ok = true;
    for (let k = 0; k < len; k++) {
      const v = src[i - (len - 1) + k];
      if (!Number.isFinite(v)) { ok = false; break; }
      wsum += v * (k + 1); // oldest weight 1 … newest weight len
    }
    if (ok) out[i] = wsum / denom;
  }
  return out;
}

/** Hull Moving Average — HMA(n) = WMA( 2·WMA(n/2) − WMA(n), round(√n) ).
 *  Mirrors Pine `ta.hma`. */
export function hmaSeries(src: number[], len: number): number[] {
  const n = src.length;
  if (len <= 1) return src.slice();
  const half = Math.max(1, Math.floor(len / 2));
  const sqrtLen = Math.max(1, Math.round(Math.sqrt(len)));
  const wHalf = wmaSeries(src, half);
  const wFull = wmaSeries(src, len);
  const diff = new Array<number>(n).fill(NaN);
  for (let i = 0; i < n; i++) {
    if (Number.isFinite(wHalf[i]) && Number.isFinite(wFull[i])) diff[i] = 2 * wHalf[i] - wFull[i];
  }
  return wmaSeries(diff, sqrtLen);
}
